
import { defineComponent, onMounted, reactive, toRefs, watch } from "vue";
import {
  fetchSites,
  fetchTurbines,
  fetchBlades,
  fetchBladeComponents,
  fetchBladeDamageTypes,
  fetchBladeHealth,
  fetchMetricTypes,
  fetchMetrics,
  fetchBladeResponse,
  postBladeDamage,
  postBladeResponse,
} from "@/helpers/api";
import {
  Sites,
  Turbine,
  Blade,
  BladeComponent,
  BladeDamageType,
  MetricType,
  Metric,
  BladeResponseRecord,
  BladeHealthRecord,
} from "@/interfaces/systemsdb";

export default defineComponent({
  name: "DamagedbPage",
  components: {},
  setup() {
    const state = reactive({
      sites: [] as Sites[],
      turbines: [] as Turbine[],
      blades: [] as Blade[],
      bladeComponents: [] as BladeComponent[],
      bladeDamageTypes: [] as BladeDamageType[],
      damageForm: {
        site_id: null as number | null,
        turbine_id: null as number | null,
        blade_id: null as number | null,
        component_id: null as number | null,
        damage_type_id: null as number | null,
        category: null as number | null,
        date: new Date().toISOString().slice(0, 10) as string,
        span_start: null as number | null,
        span_start_pct: false as boolean,
        span_end: null as number | null,
        span_end_pct: false as boolean,
        chord_start: null as number | null,
        chord_start_pct: false as boolean,
        chord_end: null as number | null,
        chord_end_pct: false as boolean,
        depth_max: null as number | null,
        depth_min: null as number | null,
      },
      loadError: "" as string,
      saving: false as boolean,
      saveError: "" as string,
      saveSuccess: false as boolean,
      bladeHealthRecords: [] as BladeHealthRecord[],
      bladeHealthLoading: false as boolean,

      structuralTurbines: [] as Turbine[],
      structuralBlades: [] as Blade[],
      metricTypes: [] as MetricType[],
      metrics: [] as Metric[],
      structuralForm: {
        site_id: null as number | null,
        turbine_id: null as number | null,
        blade_id: null as number | null,
        date_first_observed: new Date().toISOString().slice(0, 10) as string,
        metric_type_id: null as number | null,
        metric_id: null as number | null,
        notes: "" as string,
        axis: [] as string[],
        location: [] as string[],
        blade_health_ids: [] as number[],
      },
      bladeHealthIdInput: null as number | null,
      bladeResponseRecords: [] as BladeResponseRecord[],
      bladeResponseLoading: false as boolean,
      structuralSaving: false as boolean,
      structuralSaveError: "" as string,
      structuralSaveSuccess: false as boolean,
    });

    const loadSites = async () => {
      try {
        [state.sites, state.bladeComponents, state.metricTypes] = await Promise.all([
          fetchSites(undefined, true),
          fetchBladeComponents(),
          fetchMetricTypes(),
        ]);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load options";
      }
    };

    const loadStructuralTurbinesForSite = async () => {
      state.structuralForm.turbine_id = null;
      state.structuralForm.blade_id = null;
      state.structuralTurbines = [];
      state.structuralBlades = [];
      if (!state.structuralForm.site_id) return;
      try {
        state.structuralTurbines = await fetchTurbines(state.structuralForm.site_id);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load turbines";
      }
    };

    const loadStructuralBladesForTurbine = async () => {
      state.structuralForm.blade_id = null;
      state.structuralBlades = [];
      if (!state.structuralForm.turbine_id) return;
      try {
        state.structuralBlades = await fetchBlades(state.structuralForm.turbine_id);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load blades";
      }
    };

    const loadMetricsForType = async () => {
      state.structuralForm.metric_id = null;
      state.metrics = [];
      if (!state.structuralForm.metric_type_id) return;
      try {
        state.metrics = await fetchMetrics(state.structuralForm.metric_type_id);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load metrics";
      }
    };

    const resetStructuralForm = () => {
      state.structuralForm.site_id = null;
      state.structuralForm.turbine_id = null;
      state.structuralForm.blade_id = null;
      state.structuralForm.date_first_observed = new Date().toISOString().slice(0, 10);
      state.structuralForm.metric_type_id = null;
      state.structuralForm.metric_id = null;
      state.structuralForm.notes = "";
      state.structuralForm.axis = [];
      state.structuralForm.location = [];
      state.structuralForm.blade_health_ids = [];
      state.bladeHealthIdInput = null;
      state.structuralTurbines = [];
      state.structuralBlades = [];
      state.metrics = [];
    };

    const addBladeHealthId = () => {
      if (
        state.bladeHealthIdInput !== null &&
        !state.structuralForm.blade_health_ids.includes(state.bladeHealthIdInput)
      ) {
        state.structuralForm.blade_health_ids.push(state.bladeHealthIdInput);
      }
      state.bladeHealthIdInput = null;
    };

    const removeBladeHealthId = (id: number) => {
      const idx = state.structuralForm.blade_health_ids.indexOf(id);
      if (idx !== -1) state.structuralForm.blade_health_ids.splice(idx, 1);
    };

    const loadBladeResponse = async () => {
      const { site_id, turbine_id, blade_id } = state.structuralForm;
      if (!site_id && !turbine_id && !blade_id) {
        state.bladeResponseRecords = [];
        return;
      }
      state.bladeResponseLoading = true;
      try {
        state.bladeResponseRecords = await fetchBladeResponse({ site_id, turbine_id, blade_id });
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load structural records";
      } finally {
        state.bladeResponseLoading = false;
      }
    };

    watch(
      [() => state.structuralForm.site_id, () => state.structuralForm.turbine_id, () => state.structuralForm.blade_id],
      loadBladeResponse
    );

    const formatTagField = (tags: Record<string, unknown> | null, key: string): string => {
      if (!tags) return "—";
      const val = tags[key];
      if (val == null) return "—";
      return Array.isArray(val) ? (val as string[]).join(", ") || "—" : String(val);
    };

    const toggleValue = (arr: string[], value: string) => {
      const idx = arr.indexOf(value);
      if (idx === -1) arr.push(value);
      else arr.splice(idx, 1);
    };

    const saveStructural = async () => {
      state.structuralSaveError = "";
      state.structuralSaveSuccess = false;
      state.structuralSaving = true;
      try {
        await postBladeResponse({ ...state.structuralForm });
        state.structuralSaveSuccess = true;
        resetStructuralForm();
      } catch (error) {
        state.structuralSaveError = error instanceof Error ? error.message : "Failed to save structural change";
      } finally {
        state.structuralSaving = false;
      }
    };

    const loadTurbinesForSite = async () => {
      state.damageForm.turbine_id = null;
      state.damageForm.blade_id = null;
      state.turbines = [];
      state.blades = [];
      if (!state.damageForm.site_id) {
        return;
      }

      try {
        state.turbines = await fetchTurbines(state.damageForm.site_id);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load turbines";
      }
    };

    const loadBladesForTurbine = async () => {
      state.damageForm.blade_id = null;
      state.blades = [];
      if (!state.damageForm.turbine_id) {
        return;
      }

      try {
        state.blades = await fetchBlades(state.damageForm.turbine_id);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load blades";
      }
    };

    const loadDamageTypesForComponent = async () => {
      state.damageForm.damage_type_id = null;
      state.bladeDamageTypes = [];
      if (!state.damageForm.component_id) {
        return;
      }

      try {
        state.bladeDamageTypes = await fetchBladeDamageTypes(state.damageForm.component_id);
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load damage types";
      }
    };

    const loadBladeHealth = async () => {
      const { site_id, turbine_id, blade_id, component_id } = state.damageForm;
      if (!site_id && !turbine_id && !blade_id) {
        state.bladeHealthRecords = [];
        return;
      }
      state.bladeHealthLoading = true;
      try {
        state.bladeHealthRecords = await fetchBladeHealth({ site_id, turbine_id, blade_id, component_id });
      } catch (error) {
        state.loadError = error instanceof Error ? error.message : "Failed to load damage records";
      } finally {
        state.bladeHealthLoading = false;
      }
    };

    watch(
      [
        () => state.damageForm.site_id,
        () => state.damageForm.turbine_id,
        () => state.damageForm.blade_id,
        () => state.damageForm.component_id,
      ],
      loadBladeHealth
    );

    const resetForm = () => {
      state.damageForm.site_id = null;
      state.damageForm.turbine_id = null;
      state.damageForm.blade_id = null;
      state.damageForm.component_id = null;
      state.damageForm.damage_type_id = null;
      state.damageForm.category = null;
      state.damageForm.date = new Date().toISOString().slice(0, 10);
      state.damageForm.span_start = null;
      state.damageForm.span_start_pct = false;
      state.damageForm.span_end = null;
      state.damageForm.span_end_pct = false;
      state.damageForm.chord_start = null;
      state.damageForm.chord_start_pct = false;
      state.damageForm.chord_end = null;
      state.damageForm.chord_end_pct = false;
      state.damageForm.depth_max = null;
      state.damageForm.depth_min = null;
      state.turbines = [];
      state.blades = [];
      state.bladeDamageTypes = [];
    };

    const saveDamage = async (repair: boolean) => {
      state.saveError = "";
      state.saveSuccess = false;
      state.saving = true;

      try {
        await postBladeDamage({ ...state.damageForm, repair });
        state.saveSuccess = true;
        resetForm();
      } catch (error) {
        state.saveError = error instanceof Error ? error.message : "Failed to save record";
      } finally {
        state.saving = false;
      }
    };

    onMounted(loadSites);

    return {
      ...toRefs(state),
      loadTurbinesForSite,
      loadBladesForTurbine,
      loadDamageTypesForComponent,
      saveDamage,
      loadBladeHealth,
      loadStructuralTurbinesForSite,
      loadStructuralBladesForTurbine,
      loadMetricsForType,
      saveStructural,
      formatTagField,
      toggleValue,
      addBladeHealthId,
      removeBladeHealthId,
      loadBladeResponse,
    };
  },
});
